/**
 * TopBar — EINZIGE Kopfzeile der Shell (Korrektur: Personal-Vorlage hatte
 * fälschlich zwei Kopfzeilen übereinander; hier bewusst konsolidiert).
 *
 * Trägt alles: links Sidebar-Collapse-Burger + Wortmarke; rechts EnvBadge,
 * Suche (⌘K), AppSwitcher, Benutzername, Abmelden — Reihenfolge und Optik
 * nach der Technik-Vorlage (kein Avatar-Dropdown mehr).
 */
import { LogOut, Menu, Search } from "lucide-react"
import { AppSwitcher } from "@/components/AppSwitcher"
import { EnvBadge } from "@/components/shared/EnvBadge"
import { AUTH_MODE, performLogout, useAuthStore } from "@/store/auth"

interface TopBarProps {
  onOpenPalette?: () => void
  onToggleSidebar?: () => void
}

export function TopBar({ onOpenPalette, onToggleSidebar }: TopBarProps) {
  const user = useAuthStore((s) => s.user)
  const roles = user?.roles ?? []

  return (
    <div className="flex h-14 shrink-0 flex-nowrap items-center gap-3 bg-slate-900 px-4 text-white">
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label="Sidebar umschalten"
        className="shrink-0 rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
      >
        <Menu className="h-5 w-5" />
      </button>

      <a href="/" className="shrink-0 whitespace-nowrap text-sm font-semibold">
        RM<span style={{ color: "#f1c40f" }}>NC</span> Energie
      </a>

      <div className="flex flex-1 items-center justify-end gap-2">
        {/* Reihenfolge = Technik-Vorlage: EnvBadge → Suche → AppSwitcher → Name → Abmelden */}
        <EnvBadge />
        {AUTH_MODE === "dev" && (
          <span className="hidden rounded-full border border-dashed border-amber-400 px-2 py-0.5 text-xs text-amber-300 sm:inline">
            Dev-Auth
          </span>
        )}

        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Suchen oder navigieren"
          className="hidden shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 md:flex"
        >
          <span>Suchen…</span>
          <kbd className="ml-1 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium">⌘K</kbd>
        </button>
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label="Suchen oder navigieren"
          className="shrink-0 rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 md:hidden"
        >
          <Search className="h-5 w-5" />
        </button>

        <AppSwitcher currentAppKey="energie" roles={roles} />

        <span className="hidden min-w-0 max-w-[10rem] truncate text-sm text-slate-300 md:inline">
          {user?.name ?? "Nicht angemeldet"}
        </span>

        {AUTH_MODE === "logto" && (
          <button
            type="button"
            onClick={() => void performLogout()}
            aria-label="Abmelden"
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md p-2 text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden text-sm md:inline">Abmelden</span>
          </button>
        )}
      </div>
    </div>
  )
}
